import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ExportsActions from 'pages/Exports/components/ExportsActions'
import { ExportsDataTableWrapper } from 'pages/Exports/styles'
import DataTable from 'components/DataTable'
import { Table } from 'antd'
import { useLazyQuery, useMutation } from '@apollo/client'
import { displayErrors, DoLazyQuery, hasNoError } from 'globals/graphql'
import { Domiciliation } from 'globals/models/Domiciliation'
import { DateRange } from 'components/RangeDatePicker'
import { format } from 'date-fns'
import {
  CANCELLATIONS_LIST,
  CancellationsListResult,
  CancellationsListVariables
} from 'pages/Exports/components/Cancellations/queries'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { SetState } from 'globals'
import Loader, { LoaderContainer } from 'components/Loader'
import {
  CANCELLATIONS_EXPORT,
  CancellationsExportResult,
  CancellationsExportVariables
} from 'pages/Exports/components/Cancellations/mutations'

const Column = Table.Column

interface IProps {
  onShowExportConfirmation: () => void
}

const Cancellations = (props: IProps) => {
  const { onShowExportConfirmation } = props
  const { t } = useTranslation()
  const [dateRange, setDateRange] = useState<DateRange>()
  const [cancellations, setCancellations] = useState<Domiciliation[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [noMoreCancellations, setNoMoreCancellations] = useState(false)

  const [doListCancellations, cancellationsListQuery] =
    useLazyQuery<CancellationsListResult, CancellationsListVariables>(
        CANCELLATIONS_LIST,
        {
          ...displayErrors,
          onCompleted: (cancellationsList) => {
            displayErrors.onCompleted(cancellationsList)

            if (cancellationsList.cancelationsList.length > 0) {
              setCancellations((cancellations) =>
                cancellations.concat(cancellationsList.cancelationsList)
              )
            } else {
              setNoMoreCancellations(true)
            }
          }
        }
    )

  const [cancellationsExport] = useMutation<
      CancellationsExportResult, CancellationsExportVariables
    >(CANCELLATIONS_EXPORT, displayErrors)

  const onExport = (onShowExportConfirmation: () => void, dateRange?: DateRange) => () =>
    dateRange && cancellationsExport({
      variables: {
        startingDate: dateRange.startDate.toISOString(),
        endingDate: dateRange.endDate.toISOString()
      }
    }).then(
        hasNoError(() => onShowExportConfirmation())
    )

  const [sentryRef] = useInfiniteScroll({
    loading: cancellationsListQuery.loading,
    hasNextPage: !noMoreCancellations,
    delayInMs: 1000,
    rootMargin: '0px 0px 400px 0px',
    onLoadMore:
      fetchMoreCancellations(
          currentPage,
          doListCancellations,
          setCurrentPage,
          dateRange
      )
  })

  return (
    <>
      <ExportsActions
        onExport={onExport(onShowExportConfirmation, dateRange)}
        onDateRangeSelected={(dateRange) => {
          setDateRange(dateRange)
          setNoMoreCancellations(false)
        }}
      />

      {cancellations.length > 0 &&
      <ExportsDataTableWrapper>
        <DataTable
          data={cancellations}
          pageSize={10000}
        >
          <Column
            key="id"
            dataIndex="id"
            title={t('global.id')}
            className="small hide-mobile"
          />

          <Column
            key="company"
            dataIndex={['society', 'name']}
            title={
              <>
                <span>{t('global.company-name')}</span>
                <span className="title-mobile">{t('global.name')}</span>
              </>
            }
          />

          <Column
            key="siret"
            dataIndex="siretOrRna"
            title={t('global.siret')}
            className="hide-mobile"
          />

          <Column
            key="canceledAt"
            dataIndex="canceledAt"
            title={
              <>
                <span>{t('exports.cancellations.cancellation-date')}</span>
                <span className="title-mobile">{t('global.date')}</span>
              </>
            }
            render={(canceledAt) => canceledAt && format(new Date(canceledAt), 'dd/MM/yyyy')}
          />

          <Column
            key="certificate"
            dataIndex="cancelationDocumentPdf"
            title={
              <>
                <span>{t('exports.cancellations.cancellation-certificate')}</span>
                <span className="title-mobile">{t('global.certificate')}</span>
              </>
            }
            render={(certificatePdf) =>
              <a href={certificatePdf} target="_blank" rel="noreferrer">{t('global.download')}</a>
            }
          />
        </DataTable>
      </ExportsDataTableWrapper>
      }

      {dateRange && !noMoreCancellations &&
        <div>
          <LoaderContainer ref={sentryRef}>
            <Loader />
          </LoaderContainer>
        </div>
      }
    </>
  )
}

const fetchMoreCancellations = (
    page: number,
    fetch: DoLazyQuery<CancellationsListVariables>,
    setPage: SetState<number>,
    dateRange?: DateRange
) => () => {
  if (dateRange) {
    fetch({
      variables: {
        startingDate: dateRange.startDate.toISOString(),
        endingDate: dateRange.endDate.toISOString(),
        page: page
      }
    })

    setPage(page + 1)
  }
}

export default Cancellations
